import { css, keyframes } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import type { HubTooltipProps } from '@adflow/frontend/ui-common';
import { HubTooltip, InfoCircle } from '@adflow/frontend/ui-common';
import { theme, typography } from '@adflow/frontend/ui-theme';

import { ERROR_TRANSITION_FUNC, ERROR_TRANSITION_LENGTH } from '../Inputs';

const slideDown = keyframes({
	from: {
		transform: 'translateY(-110%)',
	},
	to: {
		transform: 'translateY(0)',
	},
});

const styles = {
	container: css(),
	label: css(typography.label, {
		paddingLeft: '0.75rem',
		marginBottom: '0.25rem',
		minHeight: '0.9rem',
	}),
	helperContainer: css({ minHeight: '1rem' }),
	helperText: css(typography.inputHelper, {
		fontSize: '12px',
	}),
	helperTextAnimation: css({
		animation: `${slideDown} ${ERROR_TRANSITION_LENGTH} ${ERROR_TRANSITION_FUNC} forwards`,
	}),
	helperTextError: css({
		// tw`text-palettes-error-dark` doesn't work for some reason
		color: theme.colors.palettes.error.dark,
	}),
};

export interface FormControlProps {
	outerStyle?: CSSInterpolation;
	innerStyle?: CSSInterpolation;
	label?: ReactNode;
	infoText?: ReactNode;
	infoTooltipProps?: Omit<HubTooltipProps, 'title' | 'children'>;
	tooltipText?: ReactNode;
	/**
	 * NOTE: font family and font size should not be tampered with
	 */
	labelStyle?: CSSInterpolation;
	helperText?: ReactNode;
	helperStyle?: CSSInterpolation;
	helperTextStyle?: CSSInterpolation;
	helperExtra?: ReactNode;
	/**
	 * Is this control in an error state?
	 *
	 * Remember to also pass the error prop to the input element!
	 */
	error?: boolean;
	children?: ReactNode;
	className?: string;
}

/**
 * Display a label and helper text around an input.
 */
export const FormControl = forwardRef<HTMLDivElement, FormControlProps>(
	function FormControlRender(props, ref) {
		const testid = props.error ? 'form-control-error' : 'form-control';

		const formControl = (
			<div
				className={props.className}
				css={[styles.container, props.outerStyle]}
				data-testid={testid}
				ref={ref}
			>
				{props.label ? (
					<div css={{ display: 'flex' }}>
						<label css={[styles.label, props.labelStyle]}>{props.label}</label>
						{props.infoText ? (
							<InfoCircle
								calloutText={props.infoText}
								tooltipProps={props.infoTooltipProps}
								size={14}
								css={{ marginLeft: 4 }}
							/>
						) : null}
					</div>
				) : null}

				<div css={props.innerStyle}>{props.children}</div>
				<div css={[styles.helperContainer, props.helperStyle]}>
					<div
						css={[
							styles.helperText,
							props.helperText && styles.helperTextAnimation,
							props.error && styles.helperTextError,
							props.helperTextStyle,
						]}
					>
						{props.helperText ?? ''}
					</div>
					{props.helperExtra ? (
						<div css={styles.label}>{props.helperExtra ?? ''}</div>
					) : null}
				</div>
			</div>
		);

		if (props.tooltipText) {
			return <HubTooltip title={props.tooltipText}>{formControl}</HubTooltip>;
		}

		return formControl;
	}
);
